import type { User } from '../types';
import { isCertificationsStaticPath } from '../constants/management2Certifications';
import { isManagement2NavPath } from '../constants/management2Nav';
import {
  isOutsourcingRegisterPath,
  isVmsAdminConfigGovernancePath,
} from '../constants/vmsAdminConfigNav';

/**
 * Options for `users.role_vms` / `user_vms_roles.role` (MySQL ENUM from migrations 014, 020, 027, 024, 028).
 * Empty value = no VMS access for that user.
 */
/** VMS role slug in API/DB — UI label is {@link DEPARTMENT_RM_LABEL}. */
export const DEPARTMENT_RM_ROLE = 'business_owner' as const;

/** User-visible name for the initiator / `business_owner` role. */
export const DEPARTMENT_RM_LABEL = 'Department RM';

export const VMS_ROLE_SELECT_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'vendor_manager', label: 'Vendor Manager' },
  { value: 'smf', label: 'SMF' },
  { value: 'legal', label: 'Legal' },
  { value: 'dpo', label: 'DPO' },
  { value: 'kyc_team', label: 'KYC Team' },
  { value: DEPARTMENT_RM_ROLE, label: DEPARTMENT_RM_LABEL },
  { value: 'business_head', label: 'Business Head' },
  { value: 'stakeholder', label: 'Stakeholder' },
  { value: 'finance', label: 'Finance' },
  { value: 'outsourcing_team', label: 'Outsourcing Team' },
  { value: 'admin', label: 'VMS Admin' },
];

const ROLE_VMS_ENUM_SET = new Set(
  VMS_ROLE_SELECT_OPTIONS.map((o) => o.value).filter((v) => v !== '')
);

/** Legacy slug before migration 028 (`dpia_officer` → `dpo`). */
const LEGACY_ROLE_VMS_ALIASES: Record<string, string> = {
  dpia_officer: 'dpo',
};

function resolveRoleVmsSlug(value: string): string | null {
  if (ROLE_VMS_ENUM_SET.has(value)) return value;
  return LEGACY_ROLE_VMS_ALIASES[value] ?? null;
}

/**
 * Dedupe and restrict to known VMS enum values (order preserved).
 */
export function normalizeRolesVms(input: unknown): string[] {
  if (input == null) return [];
  const raw = Array.isArray(input) ? input : typeof input === 'string' ? [input] : [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of raw) {
    const s = String(x).trim();
    const resolved = s ? resolveRoleVmsSlug(s) : null;
    if (!resolved || seen.has(resolved)) continue;
    seen.add(resolved);
    out.push(resolved);
  }
  return out;
}

const ROLE_LABEL_BY_VALUE = new Map(
  VMS_ROLE_SELECT_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label])
);

/** User-visible label for a VMS role slug (API/DB values unchanged). */
export function vmsRoleLabel(role: string | null | undefined): string {
  if (role == null || String(role).trim() === '') return '';
  const resolved = resolveRoleVmsSlug(String(role).trim());
  if (resolved) return ROLE_LABEL_BY_VALUE.get(resolved) ?? resolved;
  return String(role);
}

/** Comma-separated labels for role slugs (tables, tooltips). */
export function vmsRoleLabels(roles: string[] | null | undefined): string {
  if (!roles?.length) return '—';
  return normalizeRolesVms(roles)
    .map((r) => vmsRoleLabel(r))
    .join(', ');
}

/** Semicolon-separated role labels for bulk-upload instructions. */
export function vmsRoleLabelListForDocs(): string {
  return VMS_ROLE_SELECT_OPTIONS.filter((o) => o.value).map((o) => o.label).join('; ');
}

/** Prefer API `roles_vms`; fall back to legacy `role_vms`. */
export function getRolesVms(user: User | null | undefined): string[] {
  if (!user) return [];
  const fromArr = user.roles_vms;
  if (Array.isArray(fromArr) && fromArr.length > 0) {
    return normalizeRolesVms(fromArr);
  }
  return normalizeRolesVms(user.role_vms);
}

export function hasVmsRole(user: User | null | undefined, role: string): boolean {
  return getRolesVms(user).includes(role);
}

export function hasAnyVmsRole(user: User | null | undefined, roles: string[]): boolean {
  const mine = getRolesVms(user);
  return roles.some((r) => mine.includes(r));
}

/** True when the user has at least one assigned VMS role (from `roles_vms` / `role_vms`). */
export function hasVmsAccess(user: User | null | undefined): boolean {
  return getRolesVms(user).length > 0;
}

export type PlatformAccess = 'ims' | 'vms' | 'both';

type SuperAdminListFields = { role?: string | null; invoice_permission?: string | null };

/** Mirrors backend `isUserSuperAdminRecord` — privilege-based, not `platform_access === 'both'`. */
export function isUserSuperAdminAccount(
  user: SuperAdminListFields | User | null | undefined
): boolean {
  if (!user) return false;
  if (String(user.role ?? '') === 'super_admin') return true;
  const inv = user.invoice_permission;
  return inv === 'super_admin';
}

export function getPlatformAccess(user: User | null | undefined): PlatformAccess {
  if (isUserSuperAdminAccount(user)) return 'both';
  const p = user?.platform_access;
  if (p === 'ims' || p === 'vms' || p === 'both') return p;
  // Legacy: do not infer `both` here — avoids treating every Department RM + IMS user as governance admin.
  return hasVmsAccess(user) ? 'vms' : 'ims';
}

/** IMS invoice access + VMS roles (used for login system-selection only; mirrors backend inferPlatformAccess). */
export function userHasImsAndVmsCapability(user: User | null | undefined): boolean {
  if (!user) return false;
  return hasVmsAccess(user) && String(user.invoice_permission ?? '').trim() !== '';
}

/** Dual-platform access only — not governance super-admin (see `isUserSuperAdminAccount`). */
export function isPlatformSuperAdmin(user: User | null | undefined): boolean {
  return getPlatformAccess(user) === 'both';
}

/** Logged-in user may see super-admin rows in IMS/VMS user management lists. */
export function canSeeSuperAdminUsersInManagement(user: User | null | undefined): boolean {
  return isUserSuperAdminAccount(user);
}

/** Hide governance super-admin rows from non–super-admin viewers (IMS + VMS user management). */
export function filterUsersVisibleInManagement<T extends SuperAdminListFields>(
  users: T[],
  viewer: User | null | undefined
): T[] {
  if (canSeeSuperAdminUsersInManagement(viewer)) return users;
  return users.filter(
    (row) =>
      !isUserSuperAdminAccount({
        role: row.role ?? undefined,
        invoice_permission: row.invoice_permission ?? undefined,
      })
  );
}

export function canAccessImsPlatform(user: User | null | undefined): boolean {
  const p = getPlatformAccess(user);
  return p === 'ims' || p === 'both';
}

export function canAccessVmsPlatform(user: User | null | undefined): boolean {
  const p = getPlatformAccess(user);
  return (p === 'vms' || p === 'both') && hasVmsAccess(user);
}

/** BH / Legal / DPO — may view vendor progress only when assigned as approver (backend footprint). */
export function isWorkflowApproverSpectatorUser(user: User | null | undefined): boolean {
  return hasAnyVmsRole(user, ['business_head', 'legal', 'dpo', 'kyc_team']);
}

/**
 * BH / Legal / DPO without BO or governance admin — track vendor progress in Pre-Onboarding,
 * Onboarding, and Ongoing (BH) instead of the Register menu.
 */
export function usesApproverProgressTrackingUx(user: User | null | undefined): boolean {
  if (!isWorkflowApproverSpectatorUser(user)) return false;
  if (hasVmsRole(user, 'business_owner')) return false;
  if (isVmsGovernanceAdminUser(user)) return false;
  return true;
}

/** Pre-onboarding / onboarding progress UIs (lists and hubs); data scoped by API footprint. */
export function canAccessVmsVendorProgressSpectator(user: User | null | undefined): boolean {
  return hasAnyVmsRole(user, [
    'business_owner',
    'admin',
    'business_head',
    'stakeholder',
    'legal',
    'dpo',
    'kyc_team',
    'smf',
  ]);
}

/** Onboarding registered-vendor setup/fill (DPIA, annexure, contract): Department RM and VMS admin. */
export function canEditOnboardingRegisteredVendorForms(user: User | null | undefined): boolean {
  return hasVmsRole(user, 'business_owner') || isVmsAdminUser(user);
}

/** DPIA Part A/B fill and submit — Department RM and VMS admin (when stage is Applicable). */
export function canFillOnboardingRegisteredVendorDpiaForms(user: User | null | undefined): boolean {
  return hasVmsRole(user, 'business_owner') || isVmsAdminUser(user);
}

export function getImsDefaultLandingPath(user: User | null | undefined): string {
  if (!user) return '/ims/invoices';
  const p = user.invoice_permission;
  return p === 'admin' || p === 'super_admin' ? '/ims/dashboard' : '/ims/invoices';
}

/** True when the user may use both IMS and VMS (show system-selection cards). */
export function canUseBothPlatforms(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isUserSuperAdminAccount(user)) return hasVmsAccess(user);
  const p = user.platform_access;
  if (p === 'both') return userHasImsAndVmsCapability(user);
  if (p === 'ims' || p === 'vms') return false;
  // Unset column: offer selection when user has both capabilities (post–security-audit login alignment).
  return userHasImsAndVmsCapability(user);
}

/**
 * Where to send an authenticated user after login or on generic redirects.
 * Single-platform users skip system selection. Dual-platform users choose IMS or VMS.
 */
export function getPostLoginPath(
  user: User | null | undefined,
  options?: { freshLogin?: boolean }
): string {
  if (!canAccessVmsPlatform(user)) {
    sessionStorage.setItem('selectedSystem', 'ims');
    return getImsDefaultLandingPath(user);
  }
  if (!canUseBothPlatforms(user)) {
    sessionStorage.setItem('selectedSystem', 'vms');
    return getVmsDefaultLandingPath(user);
  }
  // IMS + VMS: always offer choice after a new login (ignore stale tab selection).
  if (options?.freshLogin) {
    sessionStorage.removeItem('selectedSystem');
    return '/system-selection';
  }
  const selectedSystem = sessionStorage.getItem('selectedSystem');
  if (selectedSystem === 'ims') return getImsDefaultLandingPath(user);
  if (selectedSystem === 'vms') return getVmsDefaultLandingPath(user);
  return '/system-selection';
}

/**
 * VMS role `admin`: governance list/view/delete; no BO create/edit unless also business_owner.
 * May approve/NMI when assigned in approver pickers (same as backend `canActAsVmsWorkflowApprover`).
 */
export function isVmsAdminUser(user: User | null | undefined): boolean {
  return hasVmsRole(user, 'admin');
}

/**
 * Mirrors backend vendor-initiations `isAdminOrSuperAdmin` for `governance_all` / `pending_queue`.
 * Dual-platform users without VMS `admin` or IMS admin/super_admin must not send `governance_all` (403).
 */
export function canUseVendorInitiationGovernanceList(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isUserSuperAdminAccount(user)) return true;
  const inv = user.invoice_permission;
  if (inv === 'admin' || inv === 'super_admin') return true;
  return isVmsAdminUser(user);
}

/**
 * Invoice / VMS governance admins — full pre-onboarding and approval-queue lists (not workspace-scoped).
 * Matches backend vendor-initiations `isAdminOrSuperAdmin` — not inferred dual-platform Department RM users.
 */
export function isVmsGovernanceAdminUser(user: User | null | undefined): boolean {
  return canUseVendorInitiationGovernanceList(user);
}

/** VMS admin with no operational VMS role (BO/BH/SH). Used to hide owner flows while allowing combined-role union UX. */
export function isPureVmsAdminUser(user: User | null | undefined): boolean {
  return (
    hasVmsRole(user, 'admin') &&
    !hasAnyVmsRole(user, ['business_owner', 'business_head', 'stakeholder'])
  );
}

/**
 * Pure stakeholder: no business owner, business head, legal, DPO, or governance admin.
 * Users with Stakeholder + Legal/DPO are not "stakeholder-only" — they keep approver nav (e.g. Ongoing).
 */
export function isStakeholderOnlyOperationalUser(user: User | null | undefined): boolean {
  if (!user || !hasVmsRole(user, 'stakeholder')) return false;
  if (hasVmsRole(user, 'business_owner') || hasVmsRole(user, 'business_head')) return false;
  if (hasAnyVmsRole(user, ['legal', 'dpo'])) return false;
  if (isVmsGovernanceAdminUser(user)) return false;
  return true;
}

/** DPO / Legal combined with Stakeholder on the same user (User Role Management). */
export function hasStakeholderWithWorkflowApproverRoles(user: User | null | undefined): boolean {
  return hasVmsRole(user, 'stakeholder') && isWorkflowApproverSpectatorUser(user);
}

/** Pre-onboarding list lead copy for multi-role stakeholder + approver users. */
export function getPreOnboardingInProgressListLead(user: User | null | undefined): string {
  if (hasStakeholderWithWorkflowApproverRoles(user)) {
    return 'Vendor services where you are assigned as a flow stakeholder and/or as an approver. Stakeholder assignments show full journey progress (including after registration); approver assignments add actions in My Tray where you owe a step.';
  }
  if (usesApproverProgressTrackingUx(user)) {
    return 'Vendor services where you are assigned as an approver, including completed pre-onboarding and registered services. Select a service above to focus on one assignment.';
  }
  if (isStakeholderOnlyOperationalUser(user)) {
    return 'Vendor services you are assigned to as a flow stakeholder. Use the service selector above to focus on one assignment.';
  }
  return 'Shows approved vendor initiations until they appear on the outsourcing register—including in-progress stages and those with financial approval completed (shown as Approved).';
}

/** Hide Ongoing tab and routes for stakeholder-only users (client: pre-onboarding, onboarding, management only). */
export function shouldHideVmsOngoingForUser(user: User | null | undefined): boolean {
  return isStakeholderOnlyOperationalUser(user);
}

/**
 * URL paths that only users with VMS role `admin` may access (governance / configuration).
 * Dashboard uses {@link canAccessVmsDashboard} from the access matrix instead.
 */
export function isVmsFlowStakeholdersPath(pathname: string): boolean {
  return (
    pathname === '/vms/flow-stakeholders' ||
    pathname === '/vms/flow-stakeholders/legacy' ||
    /^\/vms\/flow-stakeholders\/[^/]+$/.test(pathname) ||
    pathname === '/vms/management2/flow-stakeholders' ||
    /^\/vms\/management2\/flow-stakeholders\/[^/]+$/.test(pathname)
  );
}

/** Contract definition library (list / view / edit) under VMS. */
export function isVmsContractManagementPath(pathname: string): boolean {
  return pathname === '/vms/contract-management' || pathname.startsWith('/vms/contract-management/');
}

/** DPIA definition library under VMS Management flyout. */
export function isVmsDpiaManagementPath(pathname: string): boolean {
  return pathname === '/vms/dpia-management' || pathname.startsWith('/vms/dpia-management/');
}

export function isVmsAdminOnlyPath(pathname: string): boolean {
  if (isVmsAdminConfigGovernancePath(pathname)) return true;
  if (pathname.startsWith('/vms/user-management')) return true;
  if (pathname === '/vms/ongoing-management') return true;
  if (pathname === '/vms/annexure-management' || pathname.startsWith('/vms/annexure-management/')) {
    return true;
  }
  if (pathname === '/vms/dpia-management' || pathname.startsWith('/vms/dpia-management/')) {
    return true;
  }
  if (pathname === '/vms/audit-logs') return true;
  if (pathname === '/vms/vendor-initiation-field-options') return true;
  return false;
}

function vmsPathAllowedForBusinessHeadSpectator(path: string): boolean {
  return (
    path === '/vms/pre-onboarding/overview' ||
    path === '/vms/pre-onboarding/in-progress' ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-initiation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors-proposal$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-evaluation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/financial-approval$/.test(path) ||
    path === '/vms/pre-onboarding/vendor-initiation' ||
    path === '/vms/vendor-initiation-requests' ||
    path === '/vms/vendor-initiation/annexures/pending' ||
    path === '/vms/vendor-initiation/annexure-7' ||
    /^\/vms\/vendor-initiation\/annexure-7\/\d+$/.test(path) ||
    /^\/vms\/vendor-initiation\/\d+$/.test(path) ||
    path === '/vms/rfp-management' ||
    /^\/vms\/rfp-management\/\d+$/.test(path) ||
    path === '/vms/prospective-vendors' ||
    /^\/vms\/prospective-vendors\/\d+$/.test(path) ||
    /^\/vms\/vendor-proposals\/\d+$/.test(path) ||
    path === '/vms/vendor-evaluations' ||
    path === '/vms/vendor-evaluations/pending/business-head' ||
    /^\/vms\/vendor-evaluations\/\d+$/.test(path) ||
    path === '/vms/financial-approval' ||
    /^\/vms\/financial-approval\/\d+$/.test(path) ||
    /^\/vms\/financial-approval\/\d+\/edit$/.test(path) ||
    path === '/vms/registered-vendors' ||
    /^\/vms\/registered-vendors\/\d+$/.test(path) ||
    path === '/vms/onboarding' ||
    /^\/vms\/onboarding\/vendors\/\d+$/.test(path) ||
    /^\/vms\/onboarding\/vendors\/\d+\/annexures$/.test(path) ||
    path === '/vms/onboarding/annexures' ||
    path === '/vms/onboarding/contracts' ||
    path === '/vms/onboarding/contracts/review' ||
    path.startsWith('/vms/onboarding/contracts/review/') ||
    path === '/vms/onboarding/dpia' ||
    path === '/vms/onboarding2' ||
    /^\/vms\/onboarding2\/[^/]+$/.test(path) ||
    /^\/vms\/onboarding2\/[^/]+\/annexures(\/[^/]+)?$/.test(path) ||
    path.startsWith('/vms/ongoing/annexures')
  );
}

function vmsPathAllowedForStakeholderSpectator(path: string): boolean {
  return (
    path === '/vms/pre-onboarding/overview' ||
    path === '/vms/pre-onboarding/vendor-initiation' ||
    path === '/vms/pre-onboarding/in-progress' ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-initiation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/prospective-vendors-proposal$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/vendor-evaluation$/.test(path) ||
    /^\/vms\/pre-onboarding\/in-progress\/\d+\/financial-approval$/.test(path) ||
    path === '/vms/vendor-initiation' ||
    path === '/vms/vendor-initiation/annexure-7' ||
    /^\/vms\/vendor-initiation\/annexure-7\/\d+$/.test(path) ||
    path === '/vms/rfp-management' ||
    /^\/vms\/rfp-management\/\d+$/.test(path) ||
    path === '/vms/vendor-proposals/stakeholder' ||
    /^\/vms\/vendor-proposals\/\d+$/.test(path) ||
    path === '/vms/vendor-evaluations' ||
    path === '/vms/vendor-evaluations/pending/stakeholder' ||
    /^\/vms\/vendor-evaluations\/\d+$/.test(path) ||
    path === '/vms/financial-approval' ||
    /^\/vms\/financial-approval\/\d+$/.test(path) ||
    /^\/vms\/financial-approval\/\d+\/edit$/.test(path) ||
    path === '/vms/registered-vendors' ||
    /^\/vms\/registered-vendors\/\d+$/.test(path) ||
    path === '/vms/onboarding' ||
    /^\/vms\/onboarding\/vendors\/\d+$/.test(path) ||
    /^\/vms\/onboarding\/vendors\/\d+\/annexures$/.test(path) ||
    path === '/vms/onboarding/annexures' ||
    path === '/vms/onboarding/contracts' ||
    path === '/vms/onboarding/contracts/review' ||
    path.startsWith('/vms/onboarding/contracts/review/') ||
    path === '/vms/onboarding/dpia' ||
    path === '/vms/onboarding2' ||
    /^\/vms\/onboarding2\/[^/]+$/.test(path) ||
    /^\/vms\/onboarding2\/[^/]+\/annexures(\/[^/]+)?$/.test(path) ||
    path === '/vms/prospective-vendors' ||
    /^\/vms\/prospective-vendors\/\d+$/.test(path) ||
    /^\/vms\/vendor-initiation\/\d+$/.test(path)
  );
}

/** Routes allowed for pure VMS admin (full nav strip + read-only spectator access). */
export function vmsPathAllowedForPureVmsAdminOnly(path: string): boolean {
  if (
    path === '/vms' ||
    path === '/vms/dashboard' ||
    path.startsWith('/vms/dashboard/') ||
    path === '/vms/my-tray' ||
    path === '/vms/ongoing' ||
    path.startsWith('/vms/ongoing/') ||
    isOutsourcingRegisterPath(path) ||
    isVmsAdminOnlyPath(path) ||
    isVmsContractManagementPath(path) ||
    isVmsFlowStakeholdersPath(path) ||
    isCertificationsStaticPath(path) ||
    isManagement2NavPath(path)
  ) {
    return true;
  }
  return (
    vmsPathAllowedForBusinessHeadSpectator(path) ||
    vmsPathAllowedForStakeholderSpectator(path) ||
    path === '/vms/onboarding/contracts' ||
    path === '/vms/onboarding/contracts/review' ||
    path.startsWith('/vms/onboarding/contracts/review/')
  );
}

/** First meaningful VMS route after login or when redirecting away from an unauthorized page. */
export function getVmsDefaultLandingPath(user: User | null | undefined): string {
  if (!user) return '/vms';
  if (
    hasVmsRole(user, 'business_owner') ||
    hasVmsRole(user, 'business_head') ||
    hasVmsRole(user, 'stakeholder') ||
    hasVmsRole(user, 'legal') ||
    hasVmsRole(user, 'dpo') ||
    hasVmsRole(user, 'kyc_team') ||
    hasVmsRole(user, 'smf') ||
    hasVmsRole(user, 'admin')
  ) {
    return '/vms/dashboard';
  }
  return '/vms/my-tray';
}
